import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Info, Package, Award, Loader2, Phone, Percent } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { useCart } from '../../context/CartContext';
import { awardsAPI } from '../../utils/api';
import { useToast } from '../../hooks/use-toast';
import { useRecentlyViewed } from '../../hooks/useRecentlyViewed';
import RecentlyViewed from '../../components/RecentlyViewed';

const AwardsProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const { recentlyViewed, addToRecentlyViewed } = useRecentlyViewed();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [engravingText, setEngravingText] = useState('');

  // Fetch product from API
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const data = await awardsAPI.getProduct(productId);
        setProduct(data);
        // Set initial size
        if (data.sizes && data.sizes.length > 0) {
          setSelectedSize(data.sizes[0].name);
        }
        // Add to recently viewed
        addToRecentlyViewed({
          ...data,
          section: 'awards'
        });
      } catch (err) {
        console.error('Error fetching product:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId, addToRecentlyViewed]);

  // Get selected size option
  const selectedSizeOption = useMemo(() => {
    if (!product || !product.sizes || !selectedSize) return null;
    return product.sizes.find(s => s.name === selectedSize);
  }, [product, selectedSize]);

  // Get retail price for selected size (cost prices hidden from frontend)
  const basePrice = useMemo(() => {
    if (selectedSizeOption) {
      // Use retail_price (4x markup) - never show cost_price on frontend
      return selectedSizeOption.retail_price || (selectedSizeOption.cost_price || selectedSizeOption.price || 0) * 4;
    }
    if (product?.retail_price) return product.retail_price;
    if (product?.price_range?.min) return product.price_range.min;
    return 0;
  }, [selectedSizeOption, product]);

  // Get applicable bulk discount tier
  const getDiscountTier = (qty) => {
    if (!product?.bulk_pricing) return null;
    return product.bulk_pricing.find(tier => {
      if (tier.max_qty === null) {
        return qty >= tier.min_qty;
      }
      return qty >= tier.min_qty && qty <= tier.max_qty;
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const currentTier = useMemo(() => getDiscountTier(quantity), [quantity, product]);
  const isCallForPricing = currentTier?.max_qty === null && currentTier?.discount_percent === null;

  // Calculate price with bulk discount
  const calculatedPrice = useMemo(() => {
    if (isCallForPricing) return null;
    
    const discount = currentTier?.discount_percent || 0;
    const discountedPrice = basePrice * (1 - discount / 100);
    return discountedPrice * quantity;
  }, [basePrice, quantity, currentTier, isCallForPricing]);

  // Calculate unit price after discount
  const unitPrice = useMemo(() => {
    if (isCallForPricing) return null;
    const discount = currentTier?.discount_percent || 0;
    return basePrice * (1 - discount / 100);
  }, [basePrice, currentTier, isCallForPricing]);

  const handleAddToCart = () => {
    if (!product) return;
    if (isCallForPricing) {
      // Redirect to contact for large orders
      handleContactUs();
      return;
    }
    
    // For products with sizes, require size selection
    if (product.sizes?.length > 0 && !selectedSizeOption) return;

    const cartProduct = {
      ...product,
      basePrice: unitPrice,
      image: product.image
    };

    const options = {
      size: selectedSize || 'Standard',
      sizeDescription: selectedSizeOption?.name || '',
      sku: product?.internal_sku || product?.id || '',
      engravingText: engravingText || 'Custom text to be provided',
      bulkDiscount: currentTier?.discount_percent || 0
    };

    addToCart(cartProduct, options, quantity, 'awards');
    toast({
      title: "Added to cart!",
      description: `${product.name} has been added to your cart.`,
      duration: 3000,
    });
  };

  const handleBuyNow = () => {
    handleAddToCart();
    if (!isCallForPricing) {
      navigate('/cart');
    }
  };

  const handleContactUs = () => {
    // Build prefilled contact URL with product info
    const subject = encodeURIComponent(`Quote Request: ${product?.name} - ${quantity}+ units`);
    const body = encodeURIComponent(
      `Hi, I'm interested in ordering ${quantity}+ units of:\n\n` +
      `Product: ${product?.name}\n` +
      `SKU: ${product?.internal_sku || product?.id}\n` +
      `Size: ${selectedSize || 'To be confirmed'}\n\n` +
      `Please contact me with pricing for bulk orders.\n\n` +
      `Best regards`
    );
    window.location.href = `mailto:support@engraveca.com?subject=${subject}&body=${body}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory-50 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-gold-500 animate-spin" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-ivory-50 flex items-center justify-center">
        <Card className="bg-ivory-100 border border-ivory-300/50 shadow-luxury">
          <CardContent className="p-12 text-center">
            <p className="text-ivory-600 text-lg mb-4">Product not found</p>
            <Button 
              className="btn-luxury-primary" 
              onClick={() => navigate('/Awards/products')}
            >
              Browse Awards
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory-50">
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Breadcrumb */}
        <Link 
          to="/Awards/products" 
          className="inline-flex items-center text-gold-600 hover:text-gold-700 mb-8 font-body transition-colors"
          data-testid="back-to-awards"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Awards
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="space-y-4">
            <div className="bg-ivory-100 rounded-2xl overflow-hidden shadow-luxury border border-ivory-200 aspect-square flex items-center justify-center">
              <img
                src={product.image || 'https://placehold.co/500x500?text=Crystal+Award'}
                alt={product.name}
                className="w-full h-full object-contain p-8"
                data-testid="product-image"
              />
            </div>
            {/* Product Badges */}
            <div className="flex flex-wrap gap-3 justify-center">
              <span className="px-4 py-2 bg-gold-500/10 rounded-full text-gold-700 text-sm font-body flex items-center gap-2">
                <Award className="w-4 h-4" /> Premium Crystal
              </span>
              <span className="px-4 py-2 bg-ivory-200 rounded-full text-ivory-600 text-sm font-body flex items-center gap-2">
                <Package className="w-4 h-4" /> Gift Box Included
              </span>
              {product.has_bulk_discount && (
                <span className="px-4 py-2 bg-green-500/10 rounded-full text-green-700 text-sm font-body flex items-center gap-2">
                  <Percent className="w-4 h-4" /> Bulk Discounts
                </span>
              )}
            </div>
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            <div>
              <p className="text-sm text-ivory-500 font-body mb-1" data-testid="product-sku">SKU: {product.internal_sku || product.id}</p>
              <h1 className="text-4xl font-heading font-semibold text-ivory-800 mb-3" data-testid="product-name">{product.name}</h1>
              <p className="text-xl text-ivory-500 leading-relaxed font-body">{product.description}</p>
            </div>

            {/* Price Display */}
            <div className="border-t border-b border-ivory-300 py-6">
              {isCallForPricing ? (
                <div className="text-center">
                  <p className="text-2xl font-bold text-ivory-700 mb-2">100+ Units - Call for Pricing</p>
                  <p className="text-ivory-500 font-body">Contact us for custom quotes on large orders</p>
                </div>
              ) : (
                <>
                  <div className="flex items-baseline gap-3 mb-2">
                    <span className="text-4xl font-bold text-gold-600" data-testid="product-price">
                      ${calculatedPrice?.toFixed(2)}
                    </span>
                    {quantity > 1 && unitPrice && (
                      <span className="text-ivory-500 font-body">
                        (${unitPrice.toFixed(2)} each)
                      </span>
                    )}
                  </div>
                  {currentTier?.discount_percent > 0 && (
                    <p className="text-green-600 font-semibold font-body">
                      {currentTier.discount_percent}% bulk discount applied!
                    </p>
                  )}
                  {(product.price_range || product.priceRange) && product.sizes?.length > 1 && (
                    <p className="text-ivory-500 font-body mt-1">
                      Price range: ${(product.price_range?.min || product.priceRange?.min)?.toFixed(2)} - ${(product.price_range?.max || product.priceRange?.max)?.toFixed(2)}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Bulk Pricing Tiers */}
            {product.bulk_pricing && (
              <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200/50">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                    <Percent className="w-4 h-4" /> Bulk Pricing Discounts
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
                    {product.bulk_pricing.map((tier, idx) => (
                      <div 
                        key={idx}
                        className={`p-2 rounded-lg text-center ${
                          currentTier?.label === tier.label 
                            ? 'bg-green-600 text-white' 
                            : 'bg-white text-ivory-700'
                        }`}
                      >
                        <div className="font-semibold">{tier.label.split(' ')[0]}</div>
                        <div className="text-xs">
                          {tier.discount_percent !== null ? `${tier.discount_percent}% off` : 'Call'}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Options Card */}
            <Card className="gradient-champagne border border-gold-500/20">
              <CardContent className="p-6 space-y-5">
                {/* Size Selection */}
                {product.sizes && product.sizes.length > 0 && (
                  <div>
                    <Label className="text-base font-semibold mb-2 block text-ivory-800 font-body">
                      Size {product.sizes.length > 1 && `(${product.sizes.length} options)`}
                    </Label>
                    <Select value={selectedSize} onValueChange={setSelectedSize}>
                      <SelectTrigger className="bg-ivory-50 border-ivory-300 text-ivory-800 font-body" data-testid="size-select">
                        <SelectValue placeholder="Select size" />
                      </SelectTrigger>
                      <SelectContent className="bg-ivory-50 border-ivory-300">
                        {product.sizes.map((size, idx) => (
                          <SelectItem 
                            key={idx} 
                            value={size.name} 
                            className="text-ivory-700 hover:bg-gold-500/10 font-body"
                          >
                            <span className="flex flex-col">
                              <span className="font-medium">
                                {size.name} - ${(size.retail_price || (size.cost_price || size.price || 0) * 4).toFixed(2)}
                              </span>
                              {size.dimensions && (
                                <span className="text-xs text-ivory-500">
                                  {size.dimensions}
                                </span>
                              )}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedSizeOption && (
                      <div className="mt-3 p-3 bg-ivory-100 rounded-lg space-y-1 text-sm">
                        {selectedSizeOption.dimensions && (
                          <p className="text-ivory-600 font-body">
                            <span className="font-medium">Dimensions:</span> {selectedSizeOption.dimensions}
                          </p>
                        )}
                        {selectedSizeOption.etch_area && (
                          <p className="text-ivory-600 font-body">
                            <span className="font-medium">Etch Area:</span> {selectedSizeOption.etch_area}
                          </p>
                        )}
                        {selectedSizeOption.weight && (
                          <p className="text-ivory-600 font-body">
                            <span className="font-medium">Weight:</span> {selectedSizeOption.weight} {selectedSizeOption.weight_unit || 'lb'}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Quantity */}
                <div>
                  <Label className="text-base font-semibold mb-2 block text-ivory-800 font-body">Quantity</Label>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="border-ivory-300 text-ivory-700 hover:bg-ivory-200"
                      data-testid="decrease-qty"
                    >
                      -
                    </Button>
                    <Input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-20 text-center bg-ivory-50 border-ivory-300 text-ivory-800 font-body"
                      data-testid="quantity-input"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setQuantity(quantity + 1)}
                      className="border-ivory-300 text-ivory-700 hover:bg-ivory-200"
                      data-testid="increase-qty"
                    >
                      +
                    </Button>
                  </div>
                  {quantity >= 10 && !isCallForPricing && (
                    <p className="text-sm text-green-600 mt-2 font-body">
                      🎉 Bulk discount tier: {currentTier?.label}
                    </p>
                  )}
                </div>

                {/* Engraving Text */}
                <div>
                  <Label className="text-base font-semibold mb-2 block text-ivory-800 font-body">
                    Engraving Text (Optional)
                  </Label>
                  <Input
                    placeholder="Enter text to engrave..."
                    value={engravingText}
                    onChange={(e) => setEngravingText(e.target.value)}
                    className="bg-ivory-50 border-ivory-300 text-ivory-800 placeholder:text-ivory-400 font-body"
                    data-testid="engraving-input"
                  />
                  <p className="text-xs text-ivory-500 mt-1 font-body">
                    We&apos;ll contact you for artwork and engraving details after purchase
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="space-y-3">
              {isCallForPricing ? (
                <Button
                  size="lg"
                  className="w-full text-lg py-6 bg-green-600 hover:bg-green-700 text-white shadow-luxury rounded-xl"
                  onClick={handleContactUs}
                  data-testid="contact-us-btn"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Contact Us for Quote
                </Button>
              ) : (
                <>
                  <Button
                    size="lg"
                    className="w-full text-lg py-6 btn-luxury-primary shadow-luxury rounded-xl"
                    onClick={handleBuyNow}
                    data-testid="buy-now-btn"
                  >
                    Buy Now
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full text-lg py-6 btn-luxury-outline rounded-xl"
                    onClick={handleAddToCart}
                    data-testid="add-to-cart-btn"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Add to Cart
                  </Button>
                </>
              )}
            </div>

            {/* Specifications */}
            {(product.specifications || selectedSizeOption?.dimensions || selectedSizeOption?.etch_area || selectedSizeOption?.weight_lbs || product.material) && (
              <Card className="bg-ivory-100 border border-ivory-300/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Info className="w-5 h-5 text-gold-600" />
                    <h3 className="font-heading font-semibold text-lg text-ivory-800">Product Specifications</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {product.material && (
                      <div>
                        <span className="text-ivory-500 font-body">Material:</span>
                        <span className="text-ivory-800 ml-2 font-body">{product.material}</span>
                      </div>
                    )}
                    {selectedSizeOption?.dimensions && (
                      <div>
                        <span className="text-ivory-500 font-body">Dimensions (H×W×D):</span>
                        <span className="text-ivory-800 ml-2 font-body">{selectedSizeOption.dimensions}</span>
                      </div>
                    )}
                    {selectedSizeOption?.weight_lbs && (
                      <div>
                        <span className="text-ivory-500 font-body">Weight:</span>
                        <span className="text-ivory-800 ml-2 font-body">{selectedSizeOption.weight_lbs} lbs</span>
                      </div>
                    )}
                    {selectedSizeOption?.etch_area && (
                      <div>
                        <span className="text-ivory-500 font-body">Max Etch Area:</span>
                        <span className="text-ivory-800 ml-2 font-body">{selectedSizeOption.etch_area}</span>
                      </div>
                    )}
                    {/* Manufacturer SKU hidden from frontend per requirements */}
                    {product.specifications && Object.entries(product.specifications).map(([key, value]) => (
                      <div key={key}>
                        <span className="text-ivory-500 capitalize font-body">{key}:</span>
                        <span className="text-ivory-800 ml-2 font-body">{value}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Product SKU */}
            {product.internal_sku && (
              <div className="text-sm text-ivory-500 font-body">
                <p>SKU: {product.internal_sku}</p>
              </div>
            )}
          </div>
        </div>

        {/* Recently Viewed Section */}
        <RecentlyViewed products={recentlyViewed} currentProductId={productId} />
      </div>
    </div>
  );
};

export default AwardsProductDetail;
